/**
 * WritingDocumentation — unified hub for the Writing & Documentation
 * module (Phase 1: shell + reuse). Six tabs matching the spec:
 *
 *   • Style & shorthand    — org default note style + terminology
 *   • Note templates       — embeds existing NoteTemplateBuilder
 *   • Letter templates     — embeds existing LetterTemplatesSettings
 *   • Summarisation        — per-output detail level (Summarised/
 *                            Balanced/Detailed)
 *   • Signatures           — embeds existing SignatureSettings
 *   • Special reports      — Phase 3 placeholder
 *
 * Phase 2 will refactor NoteTemplateBuilder onto the spec's Template
 * + TemplateVersion + Section + Field schema and wire Style/Summary
 * into the AI generation pipeline.
 */
import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PenLine, FileText, Mail, Sparkles, Signature, FileCheck2, Save, Plus, Trash2, Info } from "lucide-react";
import { api } from "@/lib/api";

import NoteTemplateBuilder from "@/pages/NoteTemplateBuilder";
import LetterTemplatesSettings from "@/components/LetterTemplatesSettings";
import SignatureSettings from "@/components/SignatureSettings";
import SpecialReportsTab from "@/pages/SpecialReportsTab";

// Keep in sync with the backend `DEFAULT_OUTPUT_DETAIL` map.
const SUMMARISATION_OUTPUTS = [
  { key: "client_explainer", label: "Client explainer",
    hint: "Client-facing letters from your clinical notes." },
  { key: "dr_progress_update", label: "Dr progress update",
    hint: "Progress update letters to referring doctors." },
  { key: "dr_imaging_request", label: "Dr imaging / referral request",
    hint: "Imaging and referral request letters." },
  { key: "clinical_notes", label: "Clinical notes",
    hint: "Notes generated from session audio or transcript." },
];

const DETAIL_LEVELS = [
  { value: "summarised", label: "Summarised" },
  { value: "balanced", label: "Balanced" },
  { value: "detailed", label: "Detailed" },
];

export default function WritingDocumentation() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/writing/settings");
      setSettings(r.data);
    } catch (e) {
      toast.error("Couldn't load writing settings", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="writing-documentation-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-zinc-950 flex items-center gap-3">
          <PenLine className="h-8 w-8" /> Writing &amp; documentation
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Configure how the app writes notes, letters and reports on your behalf.
          Style rules apply to all generated documents. Templates and signatures
          are per-practitioner.
        </p>
      </div>

      <Tabs defaultValue="style" className="w-full">
        <TabsList className="bg-transparent p-0 border-b border-zinc-200 rounded-none h-auto justify-start gap-6">
          {[
            { v: "style", l: "Style & shorthand", i: Sparkles, t: "tab-style" },
            { v: "notes", l: "Note templates", i: FileText, t: "tab-note-templates" },
            { v: "letters", l: "Letter templates", i: Mail, t: "tab-letter-templates" },
            { v: "summary", l: "Summarisation", i: FileCheck2, t: "tab-summarisation" },
            { v: "signatures", l: "Signatures", i: Signature, t: "tab-signatures" },
            { v: "reports", l: "Special reports", i: FileCheck2, t: "tab-special-reports" },
          ].map((tab) => (
            <TabsTrigger
              key={tab.v}
              value={tab.v}
              data-testid={tab.t}
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 rounded-none px-1 pb-2 h-auto text-sm font-normal"
            >
              {tab.l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="style" className="mt-6">
          <StyleShorthandTab settings={settings} loading={loading} onSaved={load} />
        </TabsContent>

        <TabsContent value="notes" className="mt-6">
          <Card className="p-4 border-zinc-200 shadow-none">
            <NoteTemplateBuilder />
          </Card>
        </TabsContent>

        <TabsContent value="letters" className="mt-6">
          <Card className="p-4 border-zinc-200 shadow-none">
            <LetterTemplatesSettings />
          </Card>
        </TabsContent>

        <TabsContent value="summary" className="mt-6">
          <SummarisationTab settings={settings} loading={loading} onSaved={load} />
        </TabsContent>

        <TabsContent value="signatures" className="mt-6">
          <Card className="p-4 border-zinc-200 shadow-none">
            <SignatureSettings />
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          <SpecialReportsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Style & shorthand
// ---------------------------------------------------------------------------

// Mock preview text keyed by category → style → { left, right } lines.
// Kept as data (not JSX) so it's easy to eyeball and edit. Each category has
// two "chart-like" panes with category-appropriate labels so users can see
// how the same shorthand / full toggle reads in a note vs a client letter
// vs a GP letter without having to actually generate one.
const PREVIEW_CATEGORIES = [
  {
    value: "soap",
    label: "Note (SOAP)",
    hint: "How the same shorthand rule reads in a clinical note (Subjective / Objective).",
    leftHeading: "Subjective",
    rightHeading: "Objective",
    samples: {
      shorthand: {
        left: "Aggs: sitting >30min, L rotation. Eases: walking, heat. Sx: L low back pain.",
        right: "ROM: lumbar F fingertips mid-shin, pain EOR. Strength: L hip abd 4/5, pain-free.",
      },
      full: {
        left: "Aggravating factors include sitting for more than 30 minutes and left rotation. Symptoms ease with walking and heat. Reports left low back pain.",
        right: "Lumbar flexion ROM: fingertips reach mid-shin, with pain at end of range. Left hip abduction strength graded 4/5, pain-free.",
      },
    },
  },
  {
    value: "client_explainer",
    label: "Client explainer",
    hint: "How this style is softened for a plain-English client letter — telegraphic notes are always fully expanded here regardless of the toggle.",
    leftHeading: "What you told me",
    rightHeading: "What I found",
    samples: {
      // Client-facing letters intentionally read the same in both modes:
      // the shorthand rule never leaks to clients, only the clinical fields.
      shorthand: {
        left: "You mentioned that sitting for more than half an hour and turning to the left is making your lower back sore, and that walking around or applying heat helps to settle it.",
        right: "When I checked, you could bend forward until your fingertips reached mid-shin before it became uncomfortable. Your left hip strength was slightly reduced but not painful.",
      },
      full: {
        left: "You mentioned that sitting for more than half an hour and turning to the left is making your lower back sore, and that walking around or applying heat helps to settle it.",
        right: "When I checked, you could bend forward until your fingertips reached mid-shin before it became uncomfortable. Your left hip strength was slightly reduced but not painful.",
      },
    },
  },
  {
    value: "gp_letter",
    label: "GP letter",
    hint: "How this style reads in a referrer-facing progress or referral letter — always expanded to professional prose regardless of the toggle.",
    leftHeading: "History",
    rightHeading: "Examination",
    samples: {
      // GP letters also stay expanded regardless of the shorthand toggle —
      // the toggle governs *notes*, not external correspondence.
      shorthand: {
        left: "Presents with left-sided low back pain aggravated by sustained sitting greater than 30 minutes and left trunk rotation. Symptoms are eased by walking and local heat.",
        right: "On examination, lumbar flexion ROM reached mid-shin with pain reproduced at end of range. Left hip abduction strength graded 4/5, pain-free.",
      },
      full: {
        left: "Presents with left-sided low back pain aggravated by sustained sitting greater than 30 minutes and left trunk rotation. Symptoms are eased by walking and local heat.",
        right: "On examination, lumbar flexion ROM reached mid-shin with pain reproduced at end of range. Left hip abduction strength graded 4/5, pain-free.",
      },
    },
  },
];

function StyleShorthandTab({ settings, loading, onSaved }) {
  const [style, setStyle] = useState("shorthand");
  const [terminology, setTerminology] = useState([]);
  const [banned, setBanned] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewCat, setPreviewCat] = useState("soap");

  useEffect(() => {
    if (!settings) return;
    setStyle(settings.style || "shorthand");
    setTerminology(settings.terminology || []);
    setBanned((settings.banned_terms || []).join(", "));
  }, [settings]);

  async function save() {
    setSaving(true);
    try {
      await api.put("/writing/settings", {
        style,
        terminology,
        banned_terms: banned.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Style & shorthand saved");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;

  const cat = PREVIEW_CATEGORIES.find((c) => c.value === previewCat) || PREVIEW_CATEGORIES[0];
  const sample = cat.samples[style] || cat.samples.shorthand;
  const shorthandIgnored = previewCat !== "soap";

  return (
    <Card className="p-5 border-zinc-200 shadow-none space-y-5">
      <div>
        <Label className="text-sm font-medium">Note chart style</Label>
        <div className="mt-2 flex gap-2" data-testid="style-toggle">
          {["shorthand", "full"].map((v) => (
            <Button
              key={v}
              onClick={() => setStyle(v)}
              variant={style === v ? "default" : "outline"}
              className={style === v ? "bg-blue-600 hover:bg-blue-700" : ""}
              data-testid={`style-${v}`}
            >
              {v === "shorthand" ? "Shorthand" : "Full"}
            </Button>
          ))}
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          {style === "shorthand"
            ? "Telegraphic chart style — Aggs, ROM, fingertips, PoM to scar between patellas."
            : "Full professional sentences suitable for external sharing."}
        </p>
      </div>

      <div className="rounded-md border border-zinc-200 bg-zinc-50/40 p-4"
           data-testid="style-preview">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-2xs uppercase tracking-wider text-zinc-500">
            Preview
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xs text-zinc-500 hidden sm:inline">as</span>
            <Select value={previewCat} onValueChange={setPreviewCat}>
              <SelectTrigger
                className="h-7 w-[170px] text-xs"
                data-testid="preview-category-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PREVIEW_CATEGORIES.map((c) => (
                  <SelectItem
                    key={c.value}
                    value={c.value}
                    data-testid={`preview-category-${c.value}`}
                  >
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-zinc-800">
          <div>
            <div className="font-semibold uppercase text-2xs text-zinc-500 mb-1"
                 data-testid="preview-left-heading">
              {cat.leftHeading}
            </div>
            <div data-testid="preview-left-body">{sample.left}</div>
          </div>
          <div>
            <div className="font-semibold uppercase text-2xs text-zinc-500 mb-1"
                 data-testid="preview-right-heading">
              {cat.rightHeading}
            </div>
            <div data-testid="preview-right-body">{sample.right}</div>
          </div>
        </div>
        <p className="text-2xs text-zinc-500 mt-3" data-testid="preview-category-hint">
          {cat.hint}
          {shorthandIgnored && (
            <span className="ml-1 text-amber-700">
              (The shorthand toggle only affects the SOAP note itself — client and GP correspondence always reads in full prose.)
            </span>
          )}
        </p>
      </div>

      <div>
        <Label className="text-sm font-medium">Custom terminology</Label>
        <p className="text-xs text-zinc-500 mb-2">
          Approved abbreviations used across notes and letters.
        </p>
        <div className="space-y-2">
          {terminology.map((t, i) => (
            <div key={i} className="flex gap-2 items-center"
                 data-testid={`terminology-row-${i}`}>
              <Input
                value={t.abbrev}
                placeholder="e.g. ROM"
                className="w-32"
                onChange={(e) => {
                  const next = [...terminology];
                  next[i] = { ...next[i], abbrev: e.target.value };
                  setTerminology(next);
                }}
              />
              <Input
                value={t.expansion}
                placeholder="e.g. range of motion"
                onChange={(e) => {
                  const next = [...terminology];
                  next[i] = { ...next[i], expansion: e.target.value };
                  setTerminology(next);
                }}
              />
              <Button
                size="sm" variant="ghost"
                onClick={() => setTerminology(terminology.filter((_, j) => j !== i))}
                data-testid={`terminology-remove-${i}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button
            size="sm" variant="outline"
            onClick={() => setTerminology([...terminology, { abbrev: "", expansion: "" }])}
            data-testid="terminology-add"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add term
          </Button>
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium">Banned terms</Label>
        <p className="text-xs text-zinc-500 mb-2">
          Comma-separated. Never appear in generated documents.
        </p>
        <Input
          value={banned}
          onChange={(e) => setBanned(e.target.value)}
          placeholder="e.g. crippling, chronic sufferer"
          data-testid="banned-terms"
        />
      </div>

      <div>
        <Button onClick={save} disabled={saving}
                className="bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="save-style-settings">
          <Save className="h-3.5 w-3.5 mr-1" />
          {saving ? "Saving…" : "Save style settings"}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Summarisation
// ---------------------------------------------------------------------------
function SummarisationTab({ settings, loading, onSaved }) {
  const [detail, setDetail] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setDetail(settings.output_detail || {});
  }, [settings]);

  async function saveOne(key, value) {
    setDetail((prev) => ({ ...prev, [key]: value }));
    setSaving(true);
    try {
      await api.put("/writing/settings", { output_detail: { [key]: value } });
      toast.success("Detail level updated");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <Card className="p-5 border-zinc-200 shadow-none space-y-4">
      <div className="text-xs text-zinc-500 flex items-start gap-2 rounded-md bg-blue-50/60 border border-blue-200 p-3">
        <Info className="h-4 w-4 text-blue-700 shrink-0 mt-0.5" />
        <span>
          <b>Tip:</b> Richer <i>Detailed</i> notes capture more of the visit for
          your records. You can still use <i>Summarised</i> or <i>Balanced</i>
          for letters — letters draw from the note, and you can always open the
          full note for anything not shown in the letter.
        </span>
      </div>
      {SUMMARISATION_OUTPUTS.map((row) => (
        <div key={row.key} className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3 items-center border-b border-zinc-100 pb-3 last:border-0"
             data-testid={`summary-row-${row.key}`}>
          <div>
            <div className="text-sm font-medium text-zinc-900">{row.label}</div>
            <div className="text-xs text-zinc-500">{row.hint}</div>
          </div>
          <Select
            value={detail[row.key] || ""}
            onValueChange={(v) => saveOne(row.key, v)}
            disabled={saving}
          >
            <SelectTrigger data-testid={`summary-select-${row.key}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DETAIL_LEVELS.map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Special reports — Phase 3 field-mapping engine lives in SpecialReportsTab.
// ---------------------------------------------------------------------------
